import React from 'react';
import { cn } from '@/shared/utils/cn';
import { radioButtonVariants, labelVariants } from './radioButtonVariants';
import { useRadioButton } from '../../hooks/useRadioButton';
import { RadioButtonProps } from '@/shared/types/radiobutton';

/**
 * Componente RadioButton - Átomo
 *
 * Un radio button personalizable con múltiples estados y variantes de color.
 * Implementa tokens semánticos de Figma para consistencia visual.
 *
 * @example
 * ```tsx
 * <RadioButton
 *   label="Opción 1"
 *   value="option1"
 *   checked={selected === 'option1'}
 *   onChange={handleChange}
 *   color="blue"
 *   size="md"
 * />
 * ```
 */
export const RadioButton: React.FC<RadioButtonProps> = ({
  label,
  size = 'xs',
  color = 'blue',
  disabled = false,
  className,
  labelClassName,
  id,
  checked,
  onChange,
  defaultChecked,
  ...props
}) => {
  // Determinar si es controlado o no controlado
  const isControlled = checked !== undefined;
  const currentChecked = isControlled
    ? checked
    : defaultChecked !== undefined
      ? defaultChecked
      : false;

  const { state, radioId, handlers } = useRadioButton({
    checked,
    defaultChecked,
    disabled,
    size,
    color,
    onChange,
    value: String(props.value || ''),
    name: props.name,
    id,
  });

  return (
    <div
      className={cn(
        'ui-imj',
        'ui-box-border',
        'ui-flex',
        'ui-items-center',
        'ui-gap-0',
        'ui-p-0',
        'ui-rounded-control-pill',
        className,
      )}
    >
      {/* Input radio oculto para accesibilidad */}
      <input
        type="radio"
        id={radioId}
        checked={isControlled ? checked : undefined}
        defaultChecked={!isControlled && defaultChecked !== undefined ? defaultChecked : undefined}
        onChange={onChange}
        disabled={disabled}
        className="ui-sr-only"
        {...props}
      />

      {/* Diseño personalizado del RadioButton */}
      <label
        htmlFor={radioId}
        onClick={handlers.handleClick}
        onMouseDown={handlers.handleMouseDown}
        onMouseUp={handlers.handleMouseUp}
        onMouseEnter={handlers.handleMouseEnter}
        onMouseLeave={handlers.handleMouseLeave}
        className={cn(
          'ui-relative',
          'ui-inline-flex',
          'ui-items-center',
          'ui-cursor-pointer',
          'ui-select-none',
          disabled && 'ui-cursor-not-allowed',
        )}
      >
        {/* Círculo del RadioButton con estados semánticos */}
        <div
          className={cn(
            radioButtonVariants({
              size,
              color,
              state,
            }),
            'ui-shrink-0',
          )}
        />

        {/* Label con tipografía semántica */}
        {label && (
          <span className={cn(labelVariants({ size, disabled }), labelClassName)}>{label}</span>
        )}
      </label>
    </div>
  );
};
